"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowTrendingUpIcon, ClockIcon, CheckBadgeIcon, UsersIcon, ShieldCheckIcon, ChartBarSquareIcon } from '@heroicons/react/24/outline';

const MeasurableOutcomesSection = () => {
  const beforeAfter = [
    {
      icon: ClockIcon,
      before: "Slow manual reporting (days/weeks)",
      after: "Real-time insights (seconds/minutes)",
      improvement: "95% faster"
    },
    {
      icon: CheckBadgeIcon,
      before: "Poor data accuracy (60-70%)",
      after: "Significant improvement (95%+)",
      improvement: "30%+ increase"
    },
    {
      icon: UsersIcon,
      before: "Heavy manual work (40+ hrs/week)",
      after: "Dramatic reduction (5-10 hrs/week)",
      improvement: "80% reduction"
    },
    {
      icon: ArrowTrendingUpIcon,
      before: "Slow decision-making (weeks)",
      after: "Accelerated decisions (hours/days)",
      improvement: "10x faster"
    },
    {
      icon: ChartBarSquareIcon,
      before: "Siloed departments, conflicting data",
      after: "Cross-department alignment",
      improvement: "Single truth"
    },
    {
      icon: ShieldCheckIcon,
      before: "Compliance challenges and audit gaps",
      after: "Strengthened compliance readiness",
      improvement: "Audit-ready"
    }
  ];

  const metrics = [
    {
      label: "Operational Efficiency",
      before: 45,
      after: 92
    },
    {
      label: "Reporting Speed",
      before: 30,
      after: 95
    },
    {
      label: "Data Confidence",
      before: 55,
      after: 96
    }
  ];

  return (
    <section className="bg-cream py-12 sm:py-16 md:py-24 min-h-0 flex items-center">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Header */}
        <div className="text-left md:text-center mb-10 sm:mb-12 md:mb-16">
          <p className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-[1.5px] mb-3 sm:mb-4">
            PROVEN RESULTS
          </p>
          <h2 className="font-serif text-navy text-[26px] sm:text-[34px] md:text-[44px] lg:text-[48px] leading-[1.2] font-bold mb-4 sm:mb-6">
            Real Business Impact
          </h2>
          <p className="text-charcoal text-[15px] sm:text-base md:text-xl leading-[1.7] max-w-4xl md:mx-auto">
            Organizations that implement enterprise data warehousing with Simax experience transformative improvements 
            across operations, decision-making, and strategic alignment.
          </p>
        </div>

        {/* Before/After Comparison Grid */}
        <div className="mb-10 sm:mb-16">
          <h3 className="font-serif text-navy text-xl sm:text-2xl md:text-3xl font-bold text-left md:text-center mb-6 sm:mb-10">
            Transformation Outcomes
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {beforeAfter?.map((item, index) => {
              const IconComponent = item?.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-gold" />
                    </div>
                    <span className="inline-block bg-gold/10 text-gold text-xs font-bold uppercase tracking-wider px-2 py-1 rounded">
                      {item?.improvement}
                    </span>
                  </div>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="pb-2 sm:pb-3 border-b border-gray-200">
                      <span className="text-red-600 text-[11px] sm:text-xs font-semibold uppercase tracking-wider">Before</span>
                      <p className="text-charcoal text-[13px] sm:text-sm mt-1 leading-[1.5]">{item?.before}</p>
                    </div>
                    <div>
                      <span className="text-green-600 text-[11px] sm:text-xs font-semibold uppercase tracking-wider">After</span>
                      <p className="text-navy text-[13px] sm:text-sm mt-1 leading-[1.5] font-medium">{item?.after}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Metrics Visualization */}
        <div className="max-w-5xl mx-auto mb-10 sm:mb-16">
          <h3 className="font-serif text-navy text-xl sm:text-2xl md:text-3xl font-bold text-left md:text-center mb-6 sm:mb-10">
            Average Performance Improvement
          </h3>
          <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 lg:p-12 shadow-lg border border-gray-200">
            <div className="space-y-6 sm:space-y-8">
              {metrics?.map((metric, index) => (
                <div key={index}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 mb-2 sm:mb-3">
                    <span className="font-serif text-navy text-base sm:text-lg font-bold">{metric?.label}</span>
                    <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                      <span className="text-charcoal/80 text-xs sm:text-sm font-medium">Before: {metric?.before}%</span>
                      <span className="text-navy text-xs sm:text-sm font-bold">After: {metric?.after}%</span>
                    </div>
                  </div>
                  <div className="relative h-6 sm:h-8 bg-gray-200 rounded-full overflow-hidden">
                    {/* Before bar (lighter) */}
                    <div 
                      className="absolute top-0 left-0 h-full bg-gray-300 rounded-full transition-all duration-1000"
                      style={{ width: `${metric?.before}%` }}
                    ></div>
                    {/* After bar (theme gold) */}
                    <div 
                      className="absolute top-0 left-0 h-full bg-gold rounded-full transition-all duration-1000 delay-300"
                      style={{ width: `${metric?.after}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section - Redesigned */}
        <div className="max-w-6xl mx-auto">
          <div className="relative">
            <div className="absolute -top-8 -left-8 w-32 h-32 bg-gold/5 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-navy/5 rounded-full blur-3xl"></div>
            
            <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold via-navy to-gold"></div>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
                {/* Left content area - 3 columns */}
                <div className="md:col-span-3 p-5 sm:p-6 md:p-8 lg:p-12 xl:p-16">
                  <div className="mb-2 sm:mb-3">
                    <span className="inline-block w-10 sm:w-12 h-1 bg-gold rounded-full"></span>
                  </div>
                  
                  <h3 className="font-serif text-navy text-[22px] sm:text-[28px] md:text-4xl lg:text-[42px] font-bold mb-4 sm:mb-6 leading-[1.2]">
                    What Could EDW Mean for Your Organization?
                  </h3>
                  
                  <p className="text-charcoal text-[15px] sm:text-base md:text-lg leading-[1.8] mb-6 sm:mb-8">
                    Every organization's data challenges are unique. Let us analyze your specific situation and 
                    project the potential ROI of an enterprise data warehouse implementation.
                  </p>
                  
                  <Link 
                    href="/contact"
                    className="inline-flex items-center justify-center sm:justify-start gap-2 sm:gap-3 w-full sm:w-auto min-h-[48px] bg-navy hover:bg-navy/90 text-white font-bold text-[14px] sm:text-base px-6 sm:px-8 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl active:scale-[0.98] group"
                  >
                    Request Customized ROI Analysis
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                  
                  <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row flex-wrap sm:items-center gap-2 sm:gap-4 text-[13px] sm:text-sm text-charcoal/70">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gold" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>No commitment required</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gold" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Free 30-minute consultation</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gold" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Detailed analysis within 48 hours</span>
                    </div>
                  </div>
                </div>
                
                {/* Right visual area - 2 columns */}
                <div className="md:col-span-2 bg-gradient-to-br from-navy via-navy to-navy/95 p-5 sm:p-6 md:p-8 lg:p-12 flex flex-col justify-center relative overflow-hidden">
                  <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-0 right-0 w-64 h-64 border border-white rounded-full"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 border border-white rounded-full"></div>
                  </div>
                  
                  <div className="relative space-y-4 sm:space-y-6">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gold/20 flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-white font-bold text-base sm:text-lg mb-1">Projected ROI</h4>
                          <p className="text-white/70 text-xs sm:text-sm leading-relaxed">Detailed financial impact analysis tailored to your operations</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gold/20 flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-white font-bold text-base sm:text-lg mb-1">Quick Wins</h4>
                          <p className="text-white/70 text-xs sm:text-sm leading-relaxed">Identify immediate opportunities for data-driven improvements</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gold/20 flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-white font-bold text-base sm:text-lg mb-1">Implementation Roadmap</h4>
                          <p className="text-white/70 text-xs sm:text-sm leading-relaxed">Clear path from assessment to full deployment</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MeasurableOutcomesSection;